// src/pages/Appointments/Admin/VisitTracking.jsx
import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import {
  FiCheckCircle,
  FiClock,
  FiEye,
  FiShoppingBag,
  FiDollarSign,
  FiMessageSquare,
  FiX,
  FiPlay,
  FiUser,
  FiActivity,
  FiAlertCircle,
} from "react-icons/fi";
import {
  checkInCustomerThunk,
  startAppointmentThunk,
  completeVisitThunk,
  markNoShowThunk,
} from "../../../store/thunks/appointmentThunks";
import Button from "../../../components/common/Button";
import toast from "react-hot-toast";

// Import sub-components
import CheckInModal from "./VisitTracking/CheckInModal";
import ActivityTracker from "./VisitTracking/ActivityTracker";
import CompleteVisitModal from "./VisitTracking/CompleteVisitModal";
import VisitProgressBar from "./VisitTracking/VisitProgressBar";
import VisitStats from "./VisitTracking/VisitStats";

const VisitTracking = ({ appointment, onUpdate }) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);

  // Modal states
  const [showCheckInModal, setShowCheckInModal] = useState(false);
  const [showActivityTracker, setShowActivityTracker] = useState(false);
  const [showCompleteModal, setShowCompleteModal] = useState(false);

  // Visit data
  const [visitData, setVisitData] = useState({
    checkInTime: appointment.visit?.checkInTime || null,
    duration: 0,
    productsViewed: appointment.visit?.activities?.productsViewed?.length || 0,
    productsTriedOn:
      appointment.visit?.activities?.productsTriedOn?.length || 0,
  });

  // Calculate visit duration in real-time
  useEffect(() => {
    if (appointment.visit?.checkInTime && !appointment.visit?.checkOutTime) {
      const interval = setInterval(() => {
        const duration = Math.floor(
          (new Date() - new Date(appointment.visit.checkInTime)) / 60000
        );
        setVisitData((prev) => ({ ...prev, duration }));
      }, 60000); // Update every minute

      return () => clearInterval(interval);
    }
  }, [appointment.visit?.checkInTime]);

  // ========================================
  // HANDLERS
  // ========================================

  const handleCheckIn = async (checkInData) => {
    setLoading(true);
    try {
      await dispatch(
        checkInCustomerThunk({
          appointmentId: appointment._id,
          checkInData: {
            checkInMethod: checkInData.method || "ADMIN",
            notes: checkInData.notes,
          },
        })
      ).unwrap();
      toast.success("Customer checked in successfully");
      setShowCheckInModal(false);
      onUpdate();
    } catch (error) {
      toast.error(error || "Failed to check in customer");
    } finally {
      setLoading(false);
    }
  };

  const handleStart = async () => {
    setLoading(true);
    try {
      await dispatch(startAppointmentThunk(appointment._id)).unwrap();
      toast.success("Appointment started");
      setShowActivityTracker(true); // Open activity tracker
      onUpdate();
    } catch (error) {
      toast.error(error || "Failed to start appointment");
    } finally {
      setLoading(false);
    }
  };

  const handleComplete = async (completeData) => {
    setLoading(true);
    try {
      await dispatch(
        completeVisitThunk({
          appointmentId: appointment._id,
          visitData: completeData,
        })
      ).unwrap();
      toast.success("Visit completed successfully");
      setShowCompleteModal(false);
      onUpdate();
    } catch (error) {
      toast.error(error || "Failed to complete visit");
    } finally {
      setLoading(false);
    }
  };

  const handleMarkNoShow = async () => {
    if (
      !window.confirm(
        "Mark this appointment as No-Show? This action cannot be undone."
      )
    ) {
      return;
    }

    setLoading(true);
    try {
      await dispatch(
        markNoShowThunk({
          appointmentId: appointment._id,
          noShowData: {
            notes: "Customer did not show up",
            gracePeriodMinutes: 15,
            contactAttempts: [],
          },
        })
      ).unwrap();
      toast.success("Marked as no-show");
      onUpdate();
    } catch (error) {
      toast.error(error || "Failed to mark as no-show");
    } finally {
      setLoading(false);
    }
  };

  // ========================================
  // ACTION BUTTONS BY STATUS
  // ========================================

  const getActionButtons = () => {
    const status = appointment.status;

    switch (status) {
      case "CONFIRMED":
        return (
          <div className="space-y-3">
            <Button
              onClick={() => setShowCheckInModal(true)}
              loading={loading}
              className="w-full bg-green-600 hover:bg-green-700"
            >
              <FiCheckCircle className="mr-2" />
              Check In Customer
            </Button>
            <Button
              variant="outline"
              onClick={handleMarkNoShow}
              loading={loading}
              className="w-full text-red-600 border-red-600 hover:bg-red-50"
            >
              <FiX className="mr-2" />
              Mark No-Show
            </Button>
          </div>
        );

      case "CHECKED_IN":
        return (
          <Button
            onClick={handleStart}
            loading={loading}
            className="w-full bg-blue-600 hover:bg-blue-700"
          >
            <FiPlay className="mr-2" />
            Start Appointment
          </Button>
        );

      case "IN_PROGRESS":
        return (
          <div className="space-y-3">
            <Button
              onClick={() => setShowActivityTracker(true)}
              variant="outline"
              className="w-full"
            >
              <FiActivity className="mr-2" />
              Track Activities
            </Button>
            <Button
              onClick={() => setShowCompleteModal(true)}
              className="w-full bg-green-600 hover:bg-green-700"
            >
              <FiCheckCircle className="mr-2" />
              Complete Visit
            </Button>
          </div>
        );

      case "COMPLETED":
        return (
          <div className="text-center py-6 bg-green-50 rounded-lg">
            <FiCheckCircle className="mx-auto text-5xl text-green-600 mb-3" />
            <p className="text-lg font-semibold text-green-800">
              Visit Completed
            </p>
            <p className="text-sm text-green-600 mt-1">
              {appointment.visit?.checkOutTime &&
                new Date(appointment.visit.checkOutTime).toLocaleString(
                  "en-IN"
                )}
            </p>
          </div>
        );

      case "NO_SHOW":
        return (
          <div className="text-center py-6 bg-red-50 rounded-lg">
            <FiAlertCircle className="mx-auto text-5xl text-red-600 mb-3" />
            <p className="text-lg font-semibold text-red-800">No Show</p>
            <p className="text-sm text-red-600 mt-1">Customer did not arrive</p>
          </div>
        );

      default:
        return null;
    }
  };

  // ========================================
  // RENDER
  // ========================================

  return (
    <div className="bg-white rounded-lg shadow">
      {/* Header */}
      <div className="p-6 border-b">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-primary-light bg-opacity-20 flex items-center justify-center">
              <FiUser className="text-primary text-xl" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-text">
                Visit Tracking
              </h3>
              <p className="text-sm text-neutral-dark">
                Status:{" "}
                <span className="font-medium">{appointment.status}</span>
              </p>
            </div>
          </div>

          {/* Status Badge */}
          <StatusBadge status={appointment.status} />
        </div>
      </div>

      {/* Progress Bar */}
      {(appointment.status === "CHECKED_IN" ||
        appointment.status === "IN_PROGRESS" ||
        appointment.status === "COMPLETED") && (
        <div className="px-6 pt-4">
          <VisitProgressBar status={appointment.status} />
        </div>
      )}

      {/* Visit Stats */}
      {appointment.visit?.checkInTime && (
        <div className="px-6 py-4">
          <VisitStats
            visit={appointment.visit}
            duration={visitData.duration}
            scheduledDuration={appointment.duration}
          />
        </div>
      )}

      {/* Visit Timeline */}
      {appointment.visit?.checkInTime && (
        <div className="px-6 py-4 border-t">
          <h4 className="text-sm font-medium text-text mb-3">Visit Timeline</h4>
          <div className="space-y-3">
            {appointment.visit.checkInTime && (
              <TimelineItem
                label="Checked In"
                time={appointment.visit.checkInTime}
                icon={FiCheckCircle}
                color="green"
              />
            )}

            {appointment.status === "IN_PROGRESS" && (
              <TimelineItem
                label="In Progress"
                time={new Date()}
                icon={FiClock}
                color="blue"
                isActive
              />
            )}

            {appointment.visit.checkOutTime && (
              <TimelineItem
                label="Checked Out"
                time={appointment.visit.checkOutTime}
                icon={FiCheckCircle}
                color="green"
              />
            )}
          </div>
        </div>
      )}

      {/* Activity Summary */}
      {appointment.visit?.activities && (
        <div className="px-6 py-4 border-t">
          <h4 className="text-sm font-medium text-text mb-3">
            Activity Summary
          </h4>
          <div className="grid grid-cols-2 gap-3">
            <ActivityCard
              icon={FiEye}
              label="Viewed"
              count={appointment.visit.activities.productsViewed?.length || 0}
              color="blue"
            />
            <ActivityCard
              icon={FiShoppingBag}
              label="Tried On"
              count={appointment.visit.activities.productsTriedOn?.length || 0}
              color="purple"
            />
            <ActivityCard
              icon={FiShoppingBag}
              label="Added to Cart"
              count={
                appointment.visit.activities.productsAddedToCart?.length || 0
              }
              color="green"
            />
            <ActivityCard
              icon={FiMessageSquare}
              label="Interactions"
              count={appointment.visit.adminInteractions?.length || 0}
              color="orange"
            />
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="p-6 border-t">{getActionButtons()}</div>

      {/* Modals */}
      {showCheckInModal && (
        <CheckInModal
          appointment={appointment}
          onClose={() => setShowCheckInModal(false)}
          onCheckIn={handleCheckIn}
          loading={loading}
        />
      )}

      {showActivityTracker && (
        <ActivityTracker
          appointment={appointment}
          onClose={() => setShowActivityTracker(false)}
          onUpdate={onUpdate}
        />
      )}

      {showCompleteModal && (
        <CompleteVisitModal
          appointment={appointment}
          onClose={() => setShowCompleteModal(false)}
          onComplete={handleComplete}
          loading={loading}
        />
      )}
    </div>
  );
};

// ========================================
// SUB-COMPONENTS
// ========================================

const StatusBadge = ({ status }) => {
  const config = {
    CONFIRMED: {
      bg: "bg-green-100",
      text: "text-green-800",
      label: "Ready to Check In",
    },
    CHECKED_IN: {
      bg: "bg-blue-100",
      text: "text-blue-800",
      label: "Checked In",
    },
    IN_PROGRESS: {
      bg: "bg-purple-100",
      text: "text-purple-800",
      label: "In Progress",
    },
    COMPLETED: {
      bg: "bg-green-100",
      text: "text-green-800",
      label: "Completed",
    },
    NO_SHOW: { bg: "bg-red-100", text: "text-red-800", label: "No Show" },
  };

  const { bg, text, label } = config[status] || config.CONFIRMED;

  return (
    <span
      className={`px-3 py-1 rounded-full text-sm font-medium ${bg} ${text}`}
    >
      {label}
    </span>
  );
};

const TimelineItem = ({ label, time, icon: Icon, color, isActive = false }) => (
  <div className="flex items-center gap-3">
    <div
      className={`w-8 h-8 rounded-full bg-${color}-100 flex items-center justify-center ${
        isActive ? "animate-pulse" : ""
      }`}
    >
      <Icon className={`text-${color}-600`} size={16} />
    </div>
    <div className="flex-1">
      <p className="text-sm font-medium text-text">{label}</p>
      <p className="text-xs text-neutral-dark">
        {new Date(time).toLocaleTimeString("en-IN", {
          hour: "2-digit",
          minute: "2-digit",
        })}
      </p>
    </div>
  </div>
);

const ActivityCard = ({ icon: Icon, label, count, color }) => (
  <div className="p-3 bg-neutral-light rounded-lg">
    <div className="flex items-center gap-2 mb-2">
      <Icon className={`text-${color}-600`} size={16} />
      <span className="text-xs text-neutral-dark">{label}</span>
    </div>
    <p className={`text-2xl font-bold text-${color}-700`}>{count}</p>
  </div>
);

export default VisitTracking;
